import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Breadcrumb from "../../Components/Breadcrumb";
import Spinner from "../../Components/spinner";
import { getFetchData } from "../../utils/apiUtils";
import { formatDate } from "../../utils/constant";
import axiosInstance from "../../axiosInterceptor";
import { toast } from "react-toastify";
import { downloadInvoice } from "../../utils/downloadInvoice.js";
const imageUrl = process.env.REACT_APP_API_BASE_URL;

/* ── Palette ────────────────────────────────────────────────────────── */
const C = {
  text: "#0f1117",
  muted: "#6b7280",
  border: "#e5e7eb",
  surface: "#ffffff",
  green: "#16a34a",
  greenBg: "#dcfce7",
  yellow: "#b45309",
  yellowBg: "#fef3c7",
  red: "#dc2626",
  redBg: "#fee2e2",
  blue: "#1d4ed8",
  blueBg: "#dbeafe",
  grayBg: "#f3f4f6",
};

/* ── Payment status colors ──────────────────────────────────────────── */
const PAYMENT_META = {
  Unpaid: { color: C.yellow, bg: C.yellowBg },
  Paid: { color: C.green, bg: C.greenBg },
  Failed: { color: C.red, bg: C.redBg },
  Refunded: { color: C.red, bg: C.redBg },
};

/* ── Order status color by name ─────────────────────────────────────── */
const orderStatusColor = (name = "") => {
  const n = name.toLowerCase();
  if (["delivered", "shipped", "confirmed"].includes(n))
    return { color: C.green, bg: C.greenBg };
  if (["pending", "processing"].includes(n))
    return { color: C.yellow, bg: C.yellowBg };
  if (["cancelled", "returned", "failed"].includes(n))
    return { color: C.red, bg: C.redBg };
  return { color: C.blue, bg: C.blueBg };
};

/* ── Tiny reusable pieces ───────────────────────────────────────────── */
const Pill = ({ label, color, bg }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "4px 12px",
      borderRadius: 20,
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: "0.3px",
      color,
      backgroundColor: bg,
      border: `1px solid ${color}33`,
    }}
  >
    <span
      style={{
        width: 6,
        height: 6,
        borderRadius: "50%",
        background: color,
        flexShrink: 0,
      }}
    />
    {label}
  </span>
);

const SectionTitle = ({ children }) => (
  <div
    style={{
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "1.2px",
      textTransform: "uppercase",
      color: C.muted,
      marginBottom: 16,
      paddingBottom: 10,
      borderBottom: `1px solid ${C.border}`,
    }}
  >
    {children}
  </div>
);

const Field = ({ label, value }) => (
  <div style={{ marginBottom: 14 }}>
    <div
      style={{
        fontSize: 11,
        color: C.muted,
        textTransform: "uppercase",
        fontWeight: 600,
        letterSpacing: "0.6px",
        marginBottom: 3,
      }}
    >
      {label}
    </div>
    <div
      style={{ fontSize: 14, color: C.text, fontWeight: 500, lineHeight: 1.5 }}
    >
      {value || <span style={{ color: C.muted }}>—</span>}
    </div>
  </div>
);

const Panel = ({ children, style }) => (
  <div
    style={{
      background: C.surface,
      border: `1px solid ${C.border}`,
      borderRadius: 10,
      padding: "20px 24px",
      marginBottom: 20,
      ...style,
    }}
  >
    {children}
  </div>
);

/* ── Component ──────────────────────────────────────────────────────── */
const OrderDetail = () => {
  const { id } = useParams();
  const [orderTrackingLink, setOrderTrackingLink] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [allStatuses, setAllStatuses] = useState([]);
  const [statusOpen, setStatusOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const dropdownRef = useRef(null);

  const breadcrumbdata = [
    { label: "Home", path: "/dashboard" },
    { label: "Orders", path: "/orders" },
    { label: "Order Details", path: "*" },
  ];

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        await getFetchData({
          url: `/order/getOrder/${id}`,
          setGetData: setOrder,
        });
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [id]);

  useEffect(() => {
    if (order?.orderTrackingLink) {
      setOrderTrackingLink(order.orderTrackingLink);
    }
  }, [order]);
  useEffect(() => {
    getFetchData({
      url: "/orderStatus/getWithoutPaginationOrderStatus",
      setGetData: setAllStatuses,
    }).catch(console.error);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setStatusOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleStatusUpdate = async (s) => {
    if (!s || s.orderStatus === order?.orderStatusId?.orderStatus) {
      setStatusOpen(false);
      return;
    }
    setUpdating(true);
    setStatusOpen(false);
    try {
      await axiosInstance.post("/order/updateOrderStatus", {
        orderId: id,
        orderStatusId: s._id,
      });
      await getFetchData({
        url: `/order/getOrder/${id}`,
        setGetData: setOrder,
      });
    } catch (err) {
    setOrder([])
    } finally {
      setUpdating(false);
    }
  };

  /* ── Loading ── */
  if (loading) {
    return (
      <div className="content-wrapper">
        <div className="page-header">
          <h3 className="custom-page-title">Order Details</h3>
          <Breadcrumb items={breadcrumbdata} />
        </div>
        <div className="row" style={{ marginTop: 10 }}>
          <div className="col-md-12 grid-margin stretch-card">
            <div className="card">
              <div className="card-body">
                <div className="table-responsive">
                  <table className="custom-table table table-striped">
                    <tbody>
                      <tr>
                        <td className="text-center" colSpan="6">
                          <Spinner />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ── Not found ── */
  if (!order) {
    return (
      <div className="content-wrapper">
        <div
          style={{
            padding: "16px 20px",
            borderRadius: 8,
            background: C.redBg,
            color: "#991b1b",
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          Order not found.
        </div>
      </div>
    );
  }

  const handleSaveTracking = async () => {
    if (!orderTrackingLink.trim()) return;

    try {
      setSaving(true);

      // API Call
      await axiosInstance.post("/order/updateOrderTracking", {
        orderId: order._id,
        orderTrackingLink,
      });

      toast.success("Tracking number saved");
    } catch (error) {
      toast.error("Failed to save tracking number");
    } finally {
      setSaving(false);
    }
  };

  const currentOrderStatus = order?.orderStatusId?.orderStatus;
  const currentPaymentStatus = order?.paymentStatusId?.paymentStatus;
  const oMeta = orderStatusColor(currentOrderStatus);
  const pMeta = PAYMENT_META[currentPaymentStatus] ?? {
    color: C.muted,
    bg: C.grayBg,
  };

  return (
    <div className="content-wrapper">
      <div className="page-header">
        <h3 className="custom-page-title">Order Details</h3>
        <Breadcrumb items={breadcrumbdata} />
      </div>

      {/* ── Header card ── */}
      <Panel>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 16,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 20,
                fontWeight: 700,
                color: C.text,
                letterSpacing: "-0.3px",
                marginBottom: 4,
              }}
            >
              #{order.orderDisplayId}
            </div>
            <div style={{ fontSize: 13, color: C.muted }}>
              Created on {formatDate(order.createdAt)}
            </div>

            <div
              style={{
                marginTop: 12,
                display: "flex",
                alignItems: "end",
                gap: 10,
              }}
            >
              <div style={{ minWidth: 320 }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: 6,
                    fontSize: 13,
                    fontWeight: 600,
                    color: C.text,
                  }}
                >
                  Tracking Link
                </label>

                <input
                  type="text"
                  placeholder="Enter Tracking Number"
                  value={orderTrackingLink}
                  onChange={(e) => setOrderTrackingLink(e.target.value)}
                  style={{
                    width: "100%",
                    height: 46,
                    padding: "0 14px",
                    border: `1px solid ${C.border}`,
                    borderRadius: 8,
                    fontSize: 14,
                    outline: "none",
                  }}
                />
              </div>

              <button
                onClick={handleSaveTracking}
                disabled={!orderTrackingLink.trim() || saving}
                style={{
                  height: 46,
                  padding: "0 18px",
                  border: "none",
                  borderRadius: 8,
                  background:
                    !orderTrackingLink.trim() || saving ? "#d1d5db" : "#003c1f",
                  color: "#fff",
                  fontWeight: 600,
                  cursor:
                    !orderTrackingLink.trim() || saving
                      ? "not-allowed"
                      : "pointer",
                  transition: "all .2s ease",
                }}
              >
                {saving ? "Saving..." : "Save"}
              </button>
            
            </div>
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              flexDirection: "column",
              gap: 20,
              alignItems: "flex-start",
            }}
          >
            <div style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 20,
              alignItems: "flex-start",
            }}>
            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.8px",
                  textTransform: "uppercase",
                  color: C.muted,
                  marginBottom: 5,
                }}
              >
                Order Status
              </div>
              <div
                ref={dropdownRef}
                style={{ position: "relative", display: "inline-block" }}
              >
                <button
                  onClick={() => setStatusOpen((v) => !v)}
                  disabled={updating}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "4px 10px 4px 12px",
                    borderRadius: 20,
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: "0.3px",
                    color: oMeta.color,
                    backgroundColor: oMeta.bg,
                    border: `1px solid ${oMeta.color}44`,
                    cursor: updating ? "not-allowed" : "pointer",
                    outline: "none",
                  }}
                >
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: oMeta.color,
                      flexShrink: 0,
                    }}
                  />
                  {updating ? "Updating…" : currentOrderStatus}
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 10 10"
                    fill="none"
                    style={{ marginLeft: 2, flexShrink: 0 }}
                  >
                    <path
                      d={
                        statusOpen ? "M2 6.5L5 3.5L8 6.5" : "M2 3.5L5 6.5L8 3.5"
                      }
                      stroke={oMeta.color}
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>

                {statusOpen && (
                  <div
                    style={{
                      position: "absolute",
                      top: "calc(100% + 6px)",
                      left: 0,
                      zIndex: 999,
                      background: C.surface,
                      border: `1px solid ${C.border}`,
                      borderRadius: 8,
                      boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
                      minWidth: 170,
                      overflow: "hidden",
                    }}
                  >
                    {allStatuses.length === 0 ? (
                      <div
                        style={{
                          padding: "10px 14px",
                          fontSize: 12,
                          color: C.muted,
                        }}
                      >
                        Loading…
                      </div>
                    ) : (
                      allStatuses.map((s) => {
                        const m = orderStatusColor(s.orderStatus);
                        const active = s.orderStatus === currentOrderStatus;
                        return (
                          <button
                            key={s._id}
                            onClick={() => handleStatusUpdate(s)}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              width: "100%",
                              padding: "9px 14px",
                              background: active ? m.bg : "transparent",
                              border: "none",
                              cursor: "pointer",
                              fontSize: 13,
                              fontWeight: active ? 700 : 500,
                              color: active ? m.color : C.text,
                              textAlign: "left",
                            }}
                            onMouseEnter={(e) => {
                              if (!active)
                                e.currentTarget.style.background = C.grayBg;
                            }}
                            onMouseLeave={(e) => {
                              if (!active)
                                e.currentTarget.style.background =
                                  "transparent";
                            }}
                          >
                            <span
                              style={{
                                width: 7,
                                height: 7,
                                borderRadius: "50%",
                                background: m.color,
                                flexShrink: 0,
                              }}
                            />
                            {s.orderStatus}
                            {active && (
                              <svg
                                style={{ marginLeft: "auto" }}
                                width="12"
                                height="12"
                                viewBox="0 0 12 12"
                                fill="none"
                              >
                                <path
                                  d="M2 6L5 9L10 3"
                                  stroke={m.color}
                                  strokeWidth="1.8"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            )}
                          </button>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            </div>

            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.8px",
                  textTransform: "uppercase",
                  color: C.muted,
                  marginBottom: 5,
                }}
              >
                Payment Status
              </div>
              <Pill
                label={currentPaymentStatus}
                color={pMeta.color}
                bg={pMeta.bg}
              />
            </div>

            </div>
             <button
       className="btn btn-danger"
  onClick={() =>
    downloadInvoice(order)
  }
>
  Download Invoice
</button>
          </div>
        </div>
      </Panel>

      {/* ── Products + Totals ── */}
      <Panel>
        <SectionTitle>Order Items</SectionTitle>
        <div style={{ overflowX: "auto" }}>
          <table
            style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}
          >
            <thead>
              <tr>
                {[
                  "Product",
                  "Pack Size",
                  "Qty",
                  "Price",
                  "Offer Price",
                  "Total",
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: "10px 12px",
                      fontWeight: 600,
                      color: C.muted,
                      textTransform: "uppercase",
                      fontSize: 11,
                      letterSpacing: "0.6px",
                      textAlign: h === "Product" ? "left" : "right",
                      borderBottom: `2px solid ${C.border}`,
                      background: C.grayBg,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {order?.items?.map((item) => (
                <tr
                  key={item._id}
                  style={{ borderBottom: `1px solid ${C.border}` }}
                >
                  <td style={{ padding: "14px 12px" }}>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 12 }}
                    >
                      {/* Thumbnail */}
                      <div
                        style={{
                          width: 52,
                          height: 52,
                          borderRadius: 8,
                          flexShrink: 0,
                          border: `1px solid ${C.border}`,
                          overflow: "hidden",
                          background: C.grayBg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {item?.productId?.thumbnailImage?.[0] ? (
                          <>
                            <img
                              src={`${imageUrl}${item.productId.thumbnailImage[0]}`}
                              alt={item?.productId?.productTitle}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                              onError={(e) => {
                                e.target.style.display = "none";
                                e.target.nextSibling.style.display = "flex";
                              }}
                            />
                            <div
                              style={{
                                display: "none",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "100%",
                                height: "100%",
                              }}
                            >
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke={C.muted}
                                strokeWidth="1.5"
                              >
                                <rect
                                  x="3"
                                  y="3"
                                  width="18"
                                  height="18"
                                  rx="2"
                                />
                                <circle cx="8.5" cy="8.5" r="1.5" />
                                <path d="M21 15l-5-5L5 21" />
                              </svg>
                            </div>
                          </>
                        ) : (
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke={C.muted}
                            strokeWidth="1.5"
                          >
                            <rect x="3" y="3" width="18" height="18" rx="2" />
                            <circle cx="8.5" cy="8.5" r="1.5" />
                            <path d="M21 15l-5-5L5 21" />
                          </svg>
                        )}
                      </div>
                      <div>
                        <div
                          style={{
                            fontWeight: 600,
                            color: C.text,
                            marginBottom: 2,
                            lineHeight: 1.4,
                          }}
                        >
                          {item?.productId?.productTitle}
                        </div>
                        <div style={{ fontSize: 11, color: C.muted }}>
                          SKU: {item?.productId?.productSKU}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td
                    style={{
                      padding: "14px 12px",
                      textAlign: "right",
                      color: C.text,
                    }}
                  >
                    {item?.packSizeId?.packSizeName || "—"}
                  </td>
                  <td
                    style={{
                      padding: "14px 12px",
                      textAlign: "right",
                      fontWeight: 600,
                      color: C.text,
                    }}
                  >
                    {item?.bagQty}
                  </td>
                  <td
                    style={{
                      padding: "14px 12px",
                      textAlign: "right",
                      color: C.muted,
                    }}
                  >
                    ${item?.perPackPrice?.toFixed(2)}
                  </td>
                  <td
                    style={{
                      padding: "14px 12px",
                      textAlign: "right",
                      color: C.green,
                      fontWeight: 500,
                    }}
                  >
                    ${item?.perPackOfferPrice?.toFixed(2)}
                  </td>
                  <td
                    style={{
                      padding: "14px 12px",
                      textAlign: "right",
                      fontWeight: 700,
                      color: C.text,
                    }}
                  >
                    ${item?.finalPrice?.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div
          style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}
        >
          <div style={{ minWidth: 240 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 13,
                color: C.muted,
                marginBottom: 8,
              }}
            >
              <span>Subtotal</span>
              <span style={{ color: C.text, fontWeight: 500 }}>
                ${order?.subTotal?.toFixed(2)}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 13,
                color: C.muted,
                marginBottom: 12,
              }}
            >
              <span>Shipping</span>
              <span style={{ fontWeight: 500 }}>
                ${order?.shipping?.toFixed(2)}
              </span>
            </div>
            <div
              style={{
                borderTop: `1px solid ${C.border}`,
                paddingTop: 12,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ fontWeight: 700, fontSize: 14, color: C.text }}>
                Grand Total
              </span>
              <span style={{ fontWeight: 700, fontSize: 18, color: C.green }}>
                ${order?.total?.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </Panel>

      {/* ── Customer ── */}
      <Panel>
        <SectionTitle>Customer Information</SectionTitle>
        <div className="row">
          <div className="col-md-6">
            <Field
              label="Full Name"
              value={`${order?.customerId?.firstName || ""} ${order?.customerId?.lastName || ""}`.trim()}
            />
          </div>
          <div className="col-md-6">
            <Field label="Email" value={order?.customerId?.email} />
          </div>
        </div>
      </Panel>

      {/* ── Addresses ── */}
      <div className="row">
        {order?.shippingAddressId && (
          <div className="col-md-6">
            <Panel>
              <SectionTitle>Shipping Address</SectionTitle>
              <div className="row">
                <div className="col-md-6">
                  <Field
                    label="Name"
                    value={`${order?.shippingAddressId?.firstName || ""} ${order?.shippingAddressId?.lastName || ""}`.trim()}
                  />
                </div>
                <div className="col-md-6">
                  <Field
                    label="Phone"
                    value={order?.shippingAddressId?.phone}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-6">
                  <Field
                    label="Address"
                    value={[
                      order?.shippingAddressId?.streetAdd1,
                      order?.shippingAddressId?.streetAdd2,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  />
                </div>
                <div className="col-6">
                  <Field
                    label="Country"
                    value={order?.shippingAddressId?.countryId?.countryName}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-4">
                  <Field
                    label="State"
                    value={order?.shippingAddressId?.state}
                  />
                </div>
                <div className="col-4">
                  <Field label="City" value={order?.shippingAddressId?.city} />
                </div>
                <div className="col-4">
                  <Field
                    label="Zip Code"
                    value={order?.shippingAddressId?.zipCode}
                  />
                </div>
              </div>
            </Panel>
          </div>
        )}
        {order?.billingAddressId && (
          <div className="col-md-6">
            <Panel>
              <SectionTitle>Billing Address</SectionTitle>
              <div className="row">
                <div className="col-md-6">
                  <Field
                    label="Name"
                    value={`${order?.billingAddressId?.firstName || ""} ${order?.billingAddressId?.lastName || ""}`.trim()}
                  />
                </div>
                <div className="col-md-6">
                  <Field label="Phone" value={order?.billingAddressId?.phone} />
                </div>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <Field
                    label="Address"
                    value={[
                      order?.billingAddressId?.streetAdd1,
                      order?.billingAddressId?.streetAdd2,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  />
                </div>
                <div className="col-md-6">
                  <Field
                    label="Country"
                    value={order?.billingAddressId?.countryId?.countryName}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-4">
                  <Field label="State" value={order?.billingAddressId?.state} />
                </div>
                <div className="col-md-4">
                  <Field label="City" value={order?.billingAddressId?.city} />
                </div>
                <div className="col-md-4">
                  <Field
                    label="Zip Code"
                    value={order?.billingAddressId?.zipCode}
                  />
                </div>
              </div>
            </Panel>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderDetail;
